import React from "react";
import { IconButton, Menu, MenuItem, TextField } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import RemoveIcon from "@mui/icons-material/Remove";
import { CmpAdvertisingOptionFieldsFragment } from "../../../../graphql/types";
import { formatCurrency } from "../invoiceFormatting";
import { cpaGold, InvoiceMoneyTextField } from "../InvoiceMockComponents";
import { MockInvoiceLineItem } from "../mockInvoiceData";
import {
  applyCatalogOptionToLineItem,
  createEmptyLineItem,
  CUSTOM_LINE_OPTION_ID,
  isCustomLineItem,
  LineItemTaxOption,
} from "./createInvoiceHelpers";

const headerCellClass =
  "px-3 py-4 text-[13px] font-extrabold uppercase tracking-normal text-white whitespace-nowrap";

const lineItemFieldSx = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: "white",
    borderRadius: "6px",
    minHeight: 48,
  },
  "& .MuiInputBase-input": {
    fontSize: 14,
  },
};

// INV-CREATE-006: anchor line-item select menus below the field so the dropdown
// opens under the input instead of overlaying it (MUI's default selectedMenu
// behavior positions the menu on top of the trigger).
const openBelowMenuProps = {
  anchorOrigin: { vertical: "bottom", horizontal: "left" },
  transformOrigin: { vertical: "top", horizontal: "left" },
} as const;

export type LineItemFieldErrors = {
  description?: string;
  unitPrice?: string;
};

// The Create Invoice line-item table. Account # and Tax are shown for internal
// data entry but are omitted from the customer-facing invoice document.
export const InvoiceLineItemsTable = ({
  accountNumber,
  catalogOptions,
  lineItems,
  onChange,
  taxOptions,
  lineErrors,
}: {
  accountNumber: string;
  catalogOptions: CmpAdvertisingOptionFieldsFragment[];
  lineItems: MockInvoiceLineItem[];
  onChange: (lineItems: MockInvoiceLineItem[]) => void;
  taxOptions: LineItemTaxOption[];
  /** POL-019 (B5): per-line field errors keyed by line item id. */
  lineErrors?: Record<string, LineItemFieldErrors>;
}) => {
  const [catalogMenu, setCatalogMenu] = React.useState<{
    anchorEl: HTMLElement;
    lineItemId: string;
  } | null>(null);
  const defaultTaxRate =
    taxOptions.find((option) => option.ratePercentage > 0)?.ratePercentage || 0;
  const hasCatalogOptions = catalogOptions.length > 0;

  const updateLineItem = (id: string, next: Partial<MockInvoiceLineItem>) =>
    onChange(lineItems.map((item) => (item.id === id ? { ...item, ...next } : item)));

  const handleSelectOption = (id: string, optionId: string) =>
    onChange(
      lineItems.map((item) =>
        item.id === id
          ? applyCatalogOptionToLineItem({
              lineItem: item,
              optionId,
              options: catalogOptions,
            })
          : item
      )
    );
  const handleCatalogMenuSelect = (optionId: string) => {
    if (!catalogMenu) {
      return;
    }

    handleSelectOption(catalogMenu.lineItemId, optionId);
    setCatalogMenu(null);
  };

  const handleAddLineItem = () =>
    onChange([
      ...lineItems,
      createEmptyLineItem({
        accountNumber,
        defaultTaxRatePercentage: defaultTaxRate,
      }),
    ]);

  return (
    <div>
      <div className="overflow-x-auto rounded-md border border-gray-200">
        <table className="w-full min-w-[1120px] border-collapse text-left">
          <thead>
            <tr className="bg-[#1684FF]">
              <th className={headerCellClass}>Code</th>
              <th className={headerCellClass}>Description</th>
              <th className={`${headerCellClass} text-center`}>Qty</th>
              <th className={headerCellClass}>Account #</th>
              <th className={`${headerCellClass} text-right`}>Unit Price</th>
              <th className={headerCellClass}>Tax</th>
              <th className={`${headerCellClass} text-right`}>Total</th>
              <th className={headerCellClass} aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {lineItems.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-4 py-6 text-center text-sm text-gray-500">
                  No line items yet. Add one to begin.
                </td>
              </tr>
            ) : null}
            {lineItems.map((item) => {
              const isCustom = isCustomLineItem(item) || !hasCatalogOptions;
              const lineTotalInCents = item.quantity * item.unitPriceInCents;
              const itemErrors = lineErrors?.[item.id];

              return (
                <tr key={item.id} className="border-t border-gray-100 align-middle">
                  <td className="w-[110px] px-3 py-4">
                    <TextField
                      size="small"
                      placeholder="Enter Code"
                      value={item.code || ""}
                      sx={lineItemFieldSx}
                      onChange={({ target }) =>
                        updateLineItem(item.id, { code: target.value })
                      }
                      fullWidth
                    />
                  </td>
                  <td
                    className="min-w-[300px] px-3 py-4"
                    data-invoice-field-error={itemErrors?.description ? "true" : undefined}
                  >
                    {isCustom ? (
                      <div className="flex items-center gap-1">
                        <TextField
                          size="small"
                          placeholder="Enter Description"
                          value={item.description}
                          sx={lineItemFieldSx}
                          error={Boolean(itemErrors?.description)}
                          helperText={itemErrors?.description}
                          onChange={({ target }) =>
                            updateLineItem(item.id, { description: target.value })
                          }
                          fullWidth
                        />
                        {hasCatalogOptions ? (
                          <IconButton
                            aria-label="Choose from catalog"
                            size="small"
                            onClick={(event) =>
                              setCatalogMenu({
                                anchorEl: event.currentTarget,
                                lineItemId: item.id,
                              })
                            }
                            sx={{
                              border: "1px solid #D1D5DB",
                              borderRadius: "6px",
                              height: 48,
                              width: 44,
                            }}
                          >
                            <KeyboardArrowDownIcon />
                          </IconButton>
                        ) : null}
                      </div>
                    ) : (
                      <TextField
                        select
                        size="small"
                        value={item.catalogOptionId || ""}
                        sx={lineItemFieldSx}
                        error={Boolean(itemErrors?.description)}
                        helperText={itemErrors?.description}
                        onChange={({ target }) =>
                          handleSelectOption(item.id, target.value)
                        }
                        fullWidth
                        SelectProps={{
                          displayEmpty: true,
                          MenuProps: openBelowMenuProps,
                        }}
                      >
                        <MenuItem value="">Select Description</MenuItem>
                        <MenuItem value={CUSTOM_LINE_OPTION_ID}>Custom</MenuItem>
                        {catalogOptions.map((option) => (
                          <MenuItem value={option.id} key={option.id}>
                            {option.name}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  </td>
                  <td className="w-[130px] px-3 py-4">
                    <div className="mx-auto flex h-12 max-w-[116px] items-center justify-center gap-1 rounded-md border border-gray-300 bg-white">
                      <IconButton
                        aria-label="Decrease quantity"
                        size="small"
                        onClick={() =>
                          updateLineItem(item.id, {
                            quantity: Math.max(1, item.quantity - 1),
                          })
                        }
                      >
                        <RemoveIcon fontSize="small" />
                      </IconButton>
                      <span className="w-6 text-center text-sm font-semibold">
                        {item.quantity}
                      </span>
                      <IconButton
                        aria-label="Increase quantity"
                        size="small"
                        onClick={() =>
                          updateLineItem(item.id, { quantity: item.quantity + 1 })
                        }
                      >
                        <AddIcon fontSize="small" />
                      </IconButton>
                    </div>
                  </td>
                  <td className="w-[100px] px-3 py-4">
                    <div className="flex h-12 items-center justify-center rounded bg-gray-100 px-3 text-center text-sm font-semibold text-gray-500">
                      {accountNumber || "ACC#"}
                    </div>
                  </td>
                  <td
                    className="w-[140px] px-3 py-4"
                    data-invoice-field-error={itemErrors?.unitPrice ? "true" : undefined}
                  >
                    {/* POL-005 (B5): decimal text input (no spinners) with a
                        focus/blur draft model; storage stays integer cents. */}
                    <InvoiceMoneyTextField
                      valueInCents={item.unitPriceInCents}
                      sx={lineItemFieldSx}
                      ariaLabel="Unit price"
                      error={Boolean(itemErrors?.unitPrice)}
                      helperText={itemErrors?.unitPrice}
                      onValueInCentsChange={(valueInCents) =>
                        updateLineItem(item.id, { unitPriceInCents: valueInCents })
                      }
                    />
                  </td>
                  <td className="w-[160px] px-3 py-4">
                    <TextField
                      select
                      size="small"
                      value={item.taxRate}
                      sx={lineItemFieldSx}
                      onChange={({ target }) =>
                        updateLineItem(item.id, { taxRate: Number(target.value) })
                      }
                      fullWidth
                      SelectProps={{ MenuProps: openBelowMenuProps }}
                    >
                      {taxOptions.map((option) => (
                        <MenuItem value={option.ratePercentage} key={option.label}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </td>
                  <td className="w-[120px] px-3 py-4 text-right text-sm font-extrabold text-gray-900">
                    {formatCurrency(lineTotalInCents)}
                  </td>
                  <td className="w-[64px] px-3 py-4 text-center">
                    <IconButton
                      aria-label="Remove line item"
                      size="small"
                      onClick={() =>
                        onChange(lineItems.filter((current) => current.id !== item.id))
                      }
                      sx={{
                        backgroundColor: "#F31723",
                        borderRadius: "6px",
                        color: "white",
                        height: 40,
                        width: 40,
                        "&:hover": { backgroundColor: "#D90F1A" },
                      }}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <Menu
        anchorEl={catalogMenu?.anchorEl || null}
        open={Boolean(catalogMenu)}
        onClose={() => setCatalogMenu(null)}
        PaperProps={{ sx: { borderRadius: "8px", minWidth: 280, py: 1 } }}
      >
        <MenuItem onClick={() => handleCatalogMenuSelect(CUSTOM_LINE_OPTION_ID)}>
          Custom
        </MenuItem>
        {catalogOptions.map((option) => (
          <MenuItem
            key={option.id}
            onClick={() => handleCatalogMenuSelect(option.id)}
          >
            {option.name}
          </MenuItem>
        ))}
      </Menu>
      <button
        type="button"
        onClick={handleAddLineItem}
        className="mt-5 inline-flex min-h-[48px] items-center gap-2 rounded-md px-5 py-2 text-sm font-bold text-white"
        style={{ backgroundColor: cpaGold }}
      >
        <AddIcon fontSize="small" /> Add Line Item
      </button>
    </div>
  );
};
